import { useEffect } from 'react';

import { HEALTH_LABEL, ladderSteps } from '@/domain/health';
import { formatDuration } from '@/domain/time';
import type { ComponentConfig, ComponentConfigChanges, Ladder } from '@/domain/types';

/** Human labels for the configuration fields of TDD §8.4. */
const FIELD_LABEL: Record<string, string> = {
  display_name: 'Display name',
  source: 'Source',
  checks: 'Checks',
  checks_applied_at: 'Checks applied at',
  ladder: 'Escalation ladder',
  recovery_threshold_runs: 'Recovery threshold (runs)',
  auto_recovery: 'Automatic recovery',
  recovery_approval_alert_min: 'Recovery approval alert (minutes)',
  runner_cadence_minutes: 'Runner cadence (minutes)',
  staleness_tolerance_cycles: 'Staleness tolerance (missed runs)',
  applied_at: 'Applied at',
  capability_id: 'Capability id',
  scope: 'Scope',
  version: 'Version',
  updated_by: 'Updated by',
  updated_at: 'Updated at',
};

export function labelFor(key: string): string {
  return FIELD_LABEL[key] ?? key.replaceAll('_', ' ');
}

/** Audit fields change on every save; the history shows them in the entry header, not the diff. */
export const AUDIT_FIELDS = new Set([
  'version',
  'updated_by',
  'updated_at',
  'applied_at',
  'checks_applied_at',
  'scope',
  'capability_id',
]);

export function formatLadder(ladder: Ladder): string {
  const steps = ladderSteps(ladder);
  if (steps.length === 0) return '—';
  return steps.map((s) => `${s.step} → ${HEALTH_LABEL[s.health]}`).join(' · ');
}

export function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'ladder' && typeof value === 'object') return formatLadder(value as Ladder);
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function isComponentConfig(value: unknown): value is ComponentConfig {
  return typeof value === 'object' && value !== null && 'ladder' in value && 'checks' in value;
}

/** What each kind of change does once saved (TDD §8.6), for the changed keys. */
export function effectsOf(changedKeys: (keyof ComponentConfigChanges | string)[]): string[] {
  const has = (...keys: string[]) => keys.some((k) => changedKeys.includes(k));
  const effects: string[] = [];
  if (has('ladder', 'recovery_threshold_runs', 'auto_recovery')) {
    effects.push(
      'Re-evaluated at once: a proposal may be created, superseded or withdrawn immediately.',
    );
  }
  if (has('checks')) {
    effects.push(
      'Stored runs keep their judgement; new runs must carry exactly the new set. Re-evaluated at once.',
    );
  }
  if (has('source')) {
    effects.push('Runs still arriving from the old source are refused and quarantined.');
  }
  if (has('runner_cadence_minutes', 'staleness_tolerance_cycles')) {
    effects.push('Blind detection uses the new values within 5 minutes.');
  }
  return effects;
}

/** "12 × 15 min = 3 h" — the wall-clock a ladder step represents. */
export function wallClock(step: number, cadenceMinutes: number): string | null {
  if (!Number.isFinite(step) || !Number.isFinite(cadenceMinutes) || step < 1 || cadenceMinutes < 1)
    return null;
  return formatDuration(step * cadenceMinutes * 60_000);
}

/** Ask before the tab closes while edits are unsaved. */
export function useBeforeUnload(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

export function parseLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function sameArray(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function sameLadder(a: Ladder, b: Ladder): boolean {
  const sa = ladderSteps(a);
  const sb = ladderSteps(b);
  return (
    sa.length === sb.length &&
    sa.every((s, i) => s.step === sb[i]!.step && s.health === sb[i]!.health)
  );
}
